import { useState, useRef, useCallback } from 'react';
import {
  userProfileChangePassword,
  ChangePasswordFieldError,
} from '../api/userProfileChangePassword';

interface FieldState {
  hasError: boolean;
  errorMessage: string;
  value: string;
}

interface ValidationResult {
  isValid: boolean;
  message: string;
}

interface PasswordValidationState {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasSpecialChar: boolean;
  hasNumber: boolean;
}

export interface UseUpdatePasswordFormReturn {
  showCurrentPassword: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;
  currentPasswordField: FieldState;
  passwordField: FieldState;
  confirmPasswordField: FieldState;
  displayCurrentPassword: string;
  displayPassword: string;
  displayConfirmPassword: string;
  passwordValidation: PasswordValidationState;
  globalError: boolean;
  globalErrorMessage: string;
  isSubmitting: boolean;
  isPasswordUpdated: boolean;
  currentPasswordInputRef: React.RefObject<HTMLInputElement | null>;
  passwordInputRef: React.RefObject<HTMLInputElement | null>;
  confirmPasswordInputRef: React.RefObject<HTMLInputElement | null>;
  handleToggleCurrentPassword: () => void;
  handleTogglePassword: () => void;
  handleToggleConfirmPassword: () => void;
  handleCurrentPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleCloseSuccess: () => void;
}

export const useUpdatePasswordForm = (): UseUpdatePasswordFormReturn => {
  // State
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPasswordField, setCurrentPasswordField] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });
  const [passwordField, setPasswordField] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });
  const [confirmPasswordField, setConfirmPasswordField] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidationState>({
      hasMinLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasSpecialChar: false,
      hasNumber: false,
    });
  const [globalError, setGlobalError] = useState(false);
  const [globalErrorMessage, setGlobalErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);

  // Refs
  const currentPasswordInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const passwordValueRef = useRef<string>('');

  // Computed display values for password (asterisks when hidden)
  const displayCurrentPassword = showCurrentPassword
    ? currentPasswordField.value
    : '*'.repeat(currentPasswordField.value.length);
  const displayPassword = showPassword
    ? passwordField.value
    : '*'.repeat(passwordField.value.length);
  const displayConfirmPassword = showConfirmPassword
    ? confirmPasswordField.value
    : '*'.repeat(confirmPasswordField.value.length);

  // Validation functions
  const validatePasswordRequirements = useCallback(
    (password: string): PasswordValidationState => {
      return {
        hasMinLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasSpecialChar: /[^a-zA-Z0-9]/.test(password),
        hasNumber: /[0-9]/.test(password),
      };
    },
    []
  );

  const validateCurrentPassword = useCallback(
    (password: string): ValidationResult => {
      if (!password) {
        return { isValid: false, message: 'To pole jest wymagane' };
      }
      return { isValid: true, message: '' };
    },
    []
  );

  const validatePassword = useCallback(
    (password: string): ValidationResult => {
      if (!password) {
        return { isValid: false, message: 'To pole jest wymagane' };
      }
      const validation = validatePasswordRequirements(password);
      if (
        !validation.hasMinLength ||
        !validation.hasUppercase ||
        !validation.hasLowercase ||
        !validation.hasSpecialChar ||
        !validation.hasNumber
      ) {
        return {
          isValid: false,
          message: 'Password does not meet requirements',
        };
      }
      return { isValid: true, message: '' };
    },
    [validatePasswordRequirements]
  );

  const validateConfirmPassword = useCallback(
    (confirmPassword: string, password: string): ValidationResult => {
      if (!confirmPassword) {
        return { isValid: false, message: 'To pole jest wymagane' };
      }
      // Only validate match if password is not empty
      if (password === '') {
        return { isValid: true, message: '' };
      }
      if (confirmPassword !== password) {
        return { isValid: false, message: 'Hasła nie są identyczne.' };
      }
      return { isValid: true, message: '' };
    },
    []
  );

  // Handlers
  const handleToggleCurrentPassword = useCallback(() => {
    const input = currentPasswordInputRef.current;
    const cursorPosition = input?.selectionStart ?? 0;

    setShowCurrentPassword((prev) => !prev);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      input?.setSelectionRange(cursorPosition, cursorPosition);
    });
  }, []);

  const handleTogglePassword = useCallback(() => {
    const input = passwordInputRef.current;
    const cursorPosition = input?.selectionStart ?? 0;

    setShowPassword((prev) => !prev);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      input?.setSelectionRange(cursorPosition, cursorPosition);
    });
  }, []);

  const handleToggleConfirmPassword = useCallback(() => {
    const input = confirmPasswordInputRef.current;
    const cursorPosition = input?.selectionStart ?? 0;

    setShowConfirmPassword((prev) => !prev);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      input?.setSelectionRange(cursorPosition, cursorPosition);
    });
  }, []);

  const handleCurrentPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const newDisplayValue = input.value;
      const cursorPos = input.selectionStart ?? 0;

      setCurrentPasswordField((prev) => {
        const oldValue = prev.value;
        const oldLength = oldValue.length;
        const newLength = newDisplayValue.length;

        let newValue: string;

        if (showCurrentPassword) {
          newValue = newDisplayValue;
        } else {
          if (newLength > oldLength) {
            const addedChars = newDisplayValue.slice(
              cursorPos - (newLength - oldLength),
              cursorPos
            );
            newValue =
              oldValue.slice(0, cursorPos - (newLength - oldLength)) +
              addedChars +
              oldValue.slice(cursorPos - (newLength - oldLength));
          } else if (newLength < oldLength) {
            const deleteCount = oldLength - newLength;
            newValue =
              oldValue.slice(0, cursorPos) +
              oldValue.slice(cursorPos + deleteCount);
          } else {
            const typedChar = newDisplayValue[cursorPos - 1] || '';
            if (typedChar !== '*') {
              newValue =
                oldValue.slice(0, cursorPos - 1) +
                typedChar +
                oldValue.slice(cursorPos);
            } else {
              newValue = oldValue;
            }
          }
        }

        if (newValue) {
          const validation = validateCurrentPassword(newValue);
          return {
            value: newValue,
            hasError: !validation.isValid,
            errorMessage: validation.message,
          };
        } else {
          return {
            value: newValue,
            hasError: false,
            errorMessage: '',
          };
        }
      });
      setGlobalError(false);
      setGlobalErrorMessage('');

      // Restore cursor position after React re-renders
      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [validateCurrentPassword, showCurrentPassword]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const newDisplayValue = input.value;
      const cursorPos = input.selectionStart ?? 0;

      let computedNewValue: string = '';
      setPasswordField((prev) => {
        const oldValue = prev.value;
        const oldLength = oldValue.length;
        const newLength = newDisplayValue.length;

        let newValue: string;

        if (showPassword) {
          newValue = newDisplayValue;
        } else {
          if (newLength > oldLength) {
            const addedChars = newDisplayValue.slice(
              cursorPos - (newLength - oldLength),
              cursorPos
            );
            newValue =
              oldValue.slice(0, cursorPos - (newLength - oldLength)) +
              addedChars +
              oldValue.slice(cursorPos - (newLength - oldLength));
          } else if (newLength < oldLength) {
            const deleteCount = oldLength - newLength;
            newValue =
              oldValue.slice(0, cursorPos) +
              oldValue.slice(cursorPos + deleteCount);
          } else {
            const typedChar = newDisplayValue[cursorPos - 1] || '';
            if (typedChar !== '*') {
              newValue =
                oldValue.slice(0, cursorPos - 1) +
                typedChar +
                oldValue.slice(cursorPos);
            } else {
              newValue = oldValue;
            }
          }
        }

        computedNewValue = newValue;
        passwordValueRef.current = newValue;

        // Update password validation immediately with the computed value
        const passwordValidationState = validatePasswordRequirements(newValue);
        setPasswordValidation(passwordValidationState);

        return {
          ...prev,
          value: newValue,
          hasError: false,
          errorMessage: '',
        };
      });

      // Update confirm password field validation using the computed value
      // Only validate if confirm password has a value
      setConfirmPasswordField((prev) => {
        if (prev.value) {
          // If new password is empty, clear the error (don't validate match)
          if (!computedNewValue) {
            return {
              ...prev,
              hasError: false,
              errorMessage: '',
            };
          }
          const confirmValidation = validateConfirmPassword(
            prev.value,
            computedNewValue
          );
          return {
            ...prev,
            hasError: !confirmValidation.isValid,
            errorMessage: confirmValidation.message,
          };
        }
        return prev;
      });

      setGlobalError(false);
      setGlobalErrorMessage('');

      // Restore cursor position after React re-renders
      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [validatePasswordRequirements, validateConfirmPassword, showPassword]
  );

  const handleConfirmPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const newDisplayValue = input.value;
      const cursorPos = input.selectionStart ?? 0;

      setConfirmPasswordField((prev) => {
        const oldValue = prev.value;
        const oldLength = oldValue.length;
        const newLength = newDisplayValue.length;

        let newValue: string;

        if (showConfirmPassword) {
          newValue = newDisplayValue;
        } else {
          if (newLength > oldLength) {
            const addedChars = newDisplayValue.slice(
              cursorPos - (newLength - oldLength),
              cursorPos
            );
            newValue =
              oldValue.slice(0, cursorPos - (newLength - oldLength)) +
              addedChars +
              oldValue.slice(cursorPos - (newLength - oldLength));
          } else if (newLength < oldLength) {
            const deleteCount = oldLength - newLength;
            newValue =
              oldValue.slice(0, cursorPos) +
              oldValue.slice(cursorPos + deleteCount);
          } else {
            const typedChar = newDisplayValue[cursorPos - 1] || '';
            if (typedChar !== '*') {
              newValue =
                oldValue.slice(0, cursorPos - 1) +
                typedChar +
                oldValue.slice(cursorPos);
            } else {
              newValue = oldValue;
            }
          }
        }

        if (newValue) {
          // Only validate match if password field has a value
          // If password is empty, don't show mismatch error
          const validation = validateConfirmPassword(
            newValue,
            passwordField.value
          );
          return {
            value: newValue,
            hasError: !validation.isValid,
            errorMessage: validation.message,
          };
        } else {
          return {
            value: newValue,
            hasError: false,
            errorMessage: '',
          };
        }
      });
      setGlobalError(false);
      setGlobalErrorMessage('');

      // Restore cursor position after React re-renders
      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [validateConfirmPassword, passwordField.value, showConfirmPassword]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setGlobalError(false);
      setGlobalErrorMessage('');

      // Validate all fields
      const currentPasswordValidation = validateCurrentPassword(
        currentPasswordField.value
      );
      const passwordValidationResult = validatePassword(passwordField.value);
      const confirmPasswordValidationResult = validateConfirmPassword(
        confirmPasswordField.value,
        passwordField.value
      );

      setCurrentPasswordField({
        ...currentPasswordField,
        hasError: !currentPasswordValidation.isValid,
        errorMessage: currentPasswordValidation.message,
      });
      setPasswordField({
        ...passwordField,
        hasError: !passwordValidationResult.isValid,
        errorMessage: passwordValidationResult.message,
      });
      setConfirmPasswordField({
        ...confirmPasswordField,
        hasError: !confirmPasswordValidationResult.isValid,
        errorMessage: confirmPasswordValidationResult.message,
      });

      if (
        !currentPasswordValidation.isValid ||
        !passwordValidationResult.isValid ||
        !confirmPasswordValidationResult.isValid
      ) {
        return;
      }

      setIsSubmitting(true);
      setGlobalError(false);
      setGlobalErrorMessage('');

      setCurrentPasswordField((prev) => ({
        ...prev,
        hasError: false,
        errorMessage: '',
      }));
      setPasswordField((prev) => ({
        ...prev,
        hasError: false,
        errorMessage: '',
      }));
      setConfirmPasswordField((prev) => ({
        ...prev,
        hasError: false,
        errorMessage: '',
      }));

      try {
        const result = await userProfileChangePassword({
          currentPassword: currentPasswordField.value,
          newPassword: passwordField.value,
          confirmPassword: confirmPasswordField.value,
        });

        if (result.success) {
          setIsPasswordUpdated(true);
        } else {
          // Handle field-specific errors
          if (result.fieldErrors && result.fieldErrors.length > 0) {
            result.fieldErrors.forEach(
              (fieldError: ChangePasswordFieldError) => {
                if (fieldError.field === 'currentPassword') {
                  setCurrentPasswordField((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: fieldError.message,
                  }));
                } else if (fieldError.field === 'newPassword') {
                  setPasswordField((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: fieldError.message,
                  }));
                } else if (fieldError.field === 'confirmPassword') {
                  setConfirmPasswordField((prev) => ({
                    ...prev,
                    hasError: true,
                    errorMessage: fieldError.message,
                  }));
                }
              }
            );
          }

          // Handle general errors
          if (result.error) {
            setGlobalError(true);
            setGlobalErrorMessage(result.error);
          }
        }
      } catch (error) {
        setGlobalError(true);
        setGlobalErrorMessage(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      currentPasswordField,
      passwordField,
      confirmPasswordField,
      validateCurrentPassword,
      validatePassword,
      validateConfirmPassword,
    ]
  );

  const handleCloseSuccess = useCallback(() => {
    setIsPasswordUpdated(false);
    // Reset form fields
    setCurrentPasswordField({
      hasError: false,
      errorMessage: '',
      value: '',
    });
    setPasswordField({
      hasError: false,
      errorMessage: '',
      value: '',
    });
    setConfirmPasswordField({
      hasError: false,
      errorMessage: '',
      value: '',
    });
    setPasswordValidation({
      hasMinLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasSpecialChar: false,
      hasNumber: false,
    });
    passwordValueRef.current = '';
    setGlobalError(false);
    setGlobalErrorMessage('');
  }, []);

  return {
    showCurrentPassword,
    showPassword,
    showConfirmPassword,
    currentPasswordField,
    passwordField,
    confirmPasswordField,
    displayCurrentPassword,
    displayPassword,
    displayConfirmPassword,
    passwordValidation,
    globalError,
    globalErrorMessage,
    isSubmitting,
    isPasswordUpdated,
    currentPasswordInputRef,
    passwordInputRef,
    confirmPasswordInputRef,
    handleToggleCurrentPassword,
    handleTogglePassword,
    handleToggleConfirmPassword,
    handleCurrentPasswordChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    handleCloseSuccess,
  };
};
